import { useEffect, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { NewTabLink } from "components/NewTabLink";
import { SuccessTypography } from "components/SuccessTypography";
import { useWriterComponentState } from "hooks";
import { MODAL_STYLE } from "layouts/constants";
import { isValidHttpUrl } from "utils";

import {
  ClientAppEvent,
  SHIPMENT_TRACKING_LINK_BLOG_URL,
} from "../../../constants";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  shipmentId: number;
  shipmentType: ShipmentType;
  onRefresh: () => void;
}

export function AddShipmentTrackingLinkModal(props: Props) {
  const { isOpen, onClose, shipmentId, shipmentType, onRefresh } = props;
  const posthog = usePostHog();
  const [url, setUrl] = useState<string>("");
  const isURLValid = isValidHttpUrl(url);

  const { loading, error, write, success, resetState } =
    useWriterComponentState({
      errorMessage:
        "Failed to create tracking link. Please contact us if the issue persists.",
      writeConfig: {
        method: "post",
        url: `/shipments/${shipmentId}/tracking-links`,
        body: { url },
      },
      errorMessageKey: "message",
    });

  useEffect(() => {
    if (success && isOpen) {
      posthog?.capture(ClientAppEvent.SHIPMENT_TRACKING_LINK_ADDED, {
        shipment_type: shipmentType,
      });
      onRefresh();
      onClose();
      resetState();
    }
  }, [success, onRefresh, onClose, isOpen, resetState, posthog, shipmentType]);

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box sx={MODAL_STYLE}>
        <Stack justifyContent="flex-end" direction="row">
          <CloseIcon
            sx={{
              "&:hover": {
                cursor: "pointer",
              },
            }}
            onClick={onClose}
          />
        </Stack>
        <Stack direction="column" spacing={2}>
          <Typography variant="h4" component="h2" sx={{ mb: "1rem" }}>
            Add Tracking Link
          </Typography>
          <Typography variant="h6">
            Enter the URL of your load tracking software for the shipper to see.
          </Typography>

          <TextField
            fullWidth
            label="Tracking URL"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <ErrorTypography
            error={
              isURLValid ? null : "Invalid URL. URLs must begin with https:// "
            }
          />
          <span>
            {"Learn more about tracking links "}
            <NewTabLink href={SHIPMENT_TRACKING_LINK_BLOG_URL}>
              <Typography>here.</Typography>
            </NewTabLink>
          </span>
          {loading ? (
            <Stack>
              <CircularProgress />
            </Stack>
          ) : (
            <Button
              disabled={!isURLValid}
              variant="contained"
              onClick={() => write()}
            >
              Submit
            </Button>
          )}
          <SuccessTypography
            success={success}
            message="Tracking link created."
          />
          <ErrorTypography error={error} />
        </Stack>
      </Box>
    </Modal>
  );
}
